/**
 * Auth adapter — re-exports from @codespeak11/cognito-passwordless
 * with backward-compatible API for existing components.
 */
import { useEffect, type ReactNode } from 'react';
import {
  AuthProvider as CpAuthProvider,
  useAuth as cpUseAuth,
} from '@codespeak11/cognito-passwordless';
import { setAuthTokenGetter, setCurrentUserId } from '../services/api/api';

interface AuthContextValue {
  user: { sub: string; email: string } | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
}

function ApiTokenWiring({ children }: { children: ReactNode }) {
  const { getValidToken, user } = cpUseAuth();

  useEffect(() => {
    setAuthTokenGetter(getValidToken);
  }, [getValidToken]);

  useEffect(() => {
    setCurrentUserId(user?.sub ?? null);
  }, [user]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    // refreshBufferSeconds=600 → SDK proactively refreshes the access token
    // 10 minutes before it expires (the access token itself lives 60 min, set
    // on the Cognito app client). Reduces the chance the user hits a 401 while
    // actively using the SPA.
    <CpAuthProvider refreshBufferSeconds={600}>
      <ApiTokenWiring>{children}</ApiTokenWiring>
    </CpAuthProvider>
  );
}

export function useAuth(): AuthContextValue {
  const auth = cpUseAuth();
  return {
    user: auth.user ? { sub: auth.user.sub, email: auth.user.email } : null,
    accessToken: auth.accessToken,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    logout: auth.logout,
    getValidToken: auth.getValidToken,
  };
}
